import {useEffect, useMemo, useState} from 'react'
import {createUseStyles} from 'react-jss'
import {useNavigate, useParams} from "react-router-dom";
import CardSection from '../components/CardSection'
import DetailsCard from '../components/DetailsCard'
import {useAppSelector} from '../redux/hooks';
import {faArrowLeft, faCheckCircle, faClock} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import NavLink from '../components/NavLink';
import {formatDateString} from '../utils/dateHelper'
import type {DataForPayday, PayArticleInfo} from "../redux/dataForPaydayTypes";
import {titleMarginBottom} from "../constants/layout";
import {addMoney, formatMoney, subtractMoney} from "../utils/moneyHelper";
import {Colors} from "../constants/colors";
import {ColumnDef, flexRender, getCoreRowModel, useReactTable} from "@tanstack/react-table";
import DetailValueRow from '../components/DetailValueRow';

interface PayArticleListProps {
  payArticles: PayArticleInfo[]
}

interface GroupedPayArticle {
  code: string
  displayName: string
  accrualType: PayArticleInfo['accrualType']
  approved: boolean
  quantity: number
  grossPaydayValue: PayArticleInfo['grossPaydayValue']
  netPaydayValue: PayArticleInfo['netPaydayValue']
  grossCurrentValue: PayArticleInfo['grossCurrentValue']
  netCurrentValue: PayArticleInfo['netCurrentValue']
  grossValue: PayArticleInfo['grossValue']
  netValue: PayArticleInfo['netValue']
  netPayoutAvailability: PayArticleInfo['netPayoutAvailability']
  hasNetValues: boolean
}

const unavailableNetValue = '-'

const formatNetMoney = (net: PayArticleInfo['netValue'] | undefined | null, hasNetValues: boolean) =>
  hasNetValues ? formatMoney(net) : unavailableNetValue

const formatGrossNet = (
  gross: PayArticleInfo['grossValue'],
  net: PayArticleInfo['netValue'],
  hasNetValues: boolean
) => `${formatMoney(gross)} / ${formatNetMoney(net, hasNetValues)}`

const PayArticleList = ({payArticles}: PayArticleListProps) => {
  const styles = useStyles()

  const groupedPayArticles = useMemo<GroupedPayArticle[]>(() => {
    const map = new Map<string, GroupedPayArticle>()

    payArticles.forEach(payArticle => {
      const key = `${payArticle.code}-${payArticle.approved}`
      const existing = map.get(key)

      if (existing == null) {
        map.set(key, {
          code: payArticle.code,
          displayName: payArticle.displayName,
          accrualType: payArticle.accrualType,
          approved: payArticle.approved,
          quantity: payArticle.quantity,
          grossPaydayValue: {...payArticle.grossPaydayValue},
          netPaydayValue: {...payArticle.netPaydayValue},
          grossCurrentValue: {...payArticle.grossCurrentValue},
          netCurrentValue: {...payArticle.netCurrentValue},
          grossValue: {...payArticle.grossValue},
          netValue: {...payArticle.netValue},
          netPayoutAvailability: {...payArticle.netPayoutAvailability},
          hasNetValues: payArticle.hasNetValues,
        })
      } else {
        existing.quantity += payArticle.quantity
        existing.grossPaydayValue.amount += payArticle.grossPaydayValue.amount
        existing.netPaydayValue.amount += payArticle.netPaydayValue.amount
        existing.grossCurrentValue.amount += payArticle.grossCurrentValue.amount
        existing.netCurrentValue.amount += payArticle.netCurrentValue.amount
        existing.grossValue.amount += payArticle.grossValue.amount
        existing.netValue.amount += payArticle.netValue.amount
        existing.netPayoutAvailability.amount += payArticle.netPayoutAvailability.amount
        existing.hasNetValues = existing.hasNetValues && payArticle.hasNetValues
      }
    })

    return Array.from(map.values()).sort((a, b) => {
      if (a.approved !== b.approved) {
        return a.approved ? -1 : 1
      }

      return b.grossValue.amount - a.grossValue.amount
    })
  }, [payArticles])

  const columns = useMemo<ColumnDef<GroupedPayArticle>[]>(() => [
    {
      id: 'approved',
      header: '',
      cell: ({row}) => (
        <FontAwesomeIcon
          className={styles.statusIcon}
          icon={row.original.approved ? faCheckCircle : faClock}
          color={row.original.approved ? Colors.brandGreen : Colors.yen_800}
          title={row.original.approved ? 'Approved' : 'Pending approval'}
        />
      ),
    },
    {
      id: 'code',
      header: 'Code',
      cell: ({row}) => <span>{row.original.code}</span>,
    },
    {
      id: 'displayName',
      header: 'Display name',
      cell: ({row}) => <span>{row.original.displayName}</span>,
    },
    {
      id: 'accrualType',
      header: 'Accrual type',
      cell: ({row}) => <span className={styles.capitalize}>{row.original.accrualType}</span>,
    },
    {
      id: 'quantity',
      header: 'Quantity',
      cell: ({row}) => <span>{row.original.quantity}</span>,
    },
    {
      id: 'value',
      header: 'Value',
      cell: ({row}) => <span>{formatGrossNet(row.original.grossValue, row.original.netValue, row.original.hasNetValues)}</span>,
    },
    {
      id: 'currentValue',
      header: 'Current value',
      cell: ({row}) => <span>{formatGrossNet(row.original.grossCurrentValue, row.original.netCurrentValue, row.original.hasNetValues)}</span>,
    },
    {
      id: 'paydayValue',
      header: 'Payday value',
      cell: ({row}) => <span>{formatGrossNet(row.original.grossPaydayValue, row.original.netPaydayValue, row.original.hasNetValues)}</span>,
    },
    {
      id: 'payoutAvailability',
      header: 'Payout availability',
      cell: ({row}) => <span>{formatNetMoney(row.original.netPayoutAvailability, row.original.hasNetValues)}</span>,
    },
  ], [styles.capitalize, styles.statusIcon])

  const table = useReactTable({
    data: groupedPayArticles,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (groupedPayArticles.length === 0) {
    return (
      <DetailsCard>
        <h6>Pay articles</h6>
        <p>No pay articles</p>
      </DetailsCard>
    )
  }

  return (
    <div className={styles.payArticleCard}>
      <DetailsCard>
        <h6>Pay articles</h6>
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th key={header.id} className={styles.headerCell}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
            </thead>
            <tbody>
            {table.getRowModel().rows.map(row => (
              <tr className={styles.bodyRow} key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td className={styles.bodyCell} key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            </tbody>
          </table>
        </div>
      </DetailsCard>
    </div>
  )
}

const EmployeePaydayDataDetails = () => {
  const styles = useStyles()
  const params = useParams<{ employerId: string, paydayId: string }>()
  const navigate = useNavigate();

  const userDetails = useAppSelector(state => state.user.userDetails)
  const [dataForPayday, setDataForPayday] = useState<DataForPayday>()
  const totalPayArticles = useMemo<PayArticleInfo[]>(() => {
    if (dataForPayday == null) {
      return []
    }

    return [
      ...dataForPayday.payArticles.payArticles,
      ...dataForPayday.salary.employmentContracts.map(salary => salary.payArticle),
    ]
  }, [dataForPayday])
  const isLocked = dataForPayday?.payday.locked === true

  useEffect(() => {
    if (userDetails != null) {
      const dataForEmployer = userDetails.employerPaydayData.find(x => x.employerId === params.employerId)
      if (dataForEmployer != null) {
        const paydayData = dataForEmployer.paydays.find(x => x.payday.id === params.paydayId)
        if (paydayData != null) {
          setDataForPayday(paydayData)
        }
      }
    }
  }, [])

  return (
    <div className={styles.container}>
      <h1>Payday: {formatDateString(dataForPayday?.payday.paymentDate ?? '')}</h1>
      <div className={styles.actionBar}>
        <NavLink title='Back' icon={faArrowLeft} onClick={() => navigate(-1)}/>
      </div>
      <CardSection>
        <DetailsCard>
          <h6>State</h6>
          <p>Locked: <span>{dataForPayday?.payday.locked ? 'yes' : 'no'}</span></p>
          <p>Completed: <span>{dataForPayday?.payday.completed ? 'yes' : 'no'}</span></p>
          <DetailValueRow
            label='Frozen due to activation freeze period'
            value={dataForPayday?.payday.frozenByActivation ? 'yes' : 'no'}
            severity={dataForPayday?.payday.frozenByActivation ? 'warning' : undefined}
            tooltip='Payday is frozen due to activation freeze period'
          />
          <DetailValueRow
            label='Frozen due to payout basis generation'
            value={dataForPayday?.payday.frozenByEarlyPayoutBasisGeneration ? 'yes' : 'no'}
            severity={dataForPayday?.payday.frozenByEarlyPayoutBasisGeneration ? 'warning' : undefined}
            tooltip='Payday is frozen due to payout basis generation'
          />
        </DetailsCard>
        <DetailsCard>
          <h6>Preliminary income & tax</h6>
          <p>Gross total income: <span>{formatMoney(dataForPayday?.income.grossTotalAmount)}</span></p>
          <p>Net total income: <span>{formatNetMoney(dataForPayday?.income.netTotalAmount, !isLocked)}</span></p>
          {dataForPayday?.salary.containsSalary &&
              <>
                  <p>Gross salary: <span>{formatMoney(dataForPayday?.salary.grossCurrentValue)}</span></p>
                  <p>Net salary: <span>{formatNetMoney(dataForPayday?.salary.netCurrentValue, !isLocked)}</span></p>
              </>
          }
          <p>Tax: <span>{formatNetMoney(subtractMoney(dataForPayday?.income.grossTotalAmount, dataForPayday?.income.netTotalAmount), !isLocked)}</span>
          </p>
        </DetailsCard>
        <DetailsCard>
          <h6>Payouts & fees</h6>
          <p>Total payout amount: <span>{formatMoney(dataForPayday?.payoutHistory.aggregatedAmount)}</span></p>
          <p>Payout amount in month before payday: <span>{formatMoney(dataForPayday?.payoutHistory.aggregatedAmountMonthBeforePayday)}</span></p>
          <p>Payout amount in month of payday: <span>{formatMoney(subtractMoney(dataForPayday?.payoutHistory.aggregatedAmount, dataForPayday?.payoutHistory.aggregatedAmountMonthBeforePayday))}</span></p>
          <p>Payout count: <span>{dataForPayday?.payoutHistory.payoutCount}</span></p>
          <p>Fees: <span>{formatMoney(dataForPayday?.payoutHistory.aggregatedFeeAmount)}</span></p>
          <p>Total debt: <span>{formatMoney(addMoney(dataForPayday?.payoutHistory.aggregatedAmount, dataForPayday?.payoutHistory.aggregatedFeeAmount))}</span></p>
        </DetailsCard>
        {!isLocked &&
            <>
                <DetailsCard>
                    <h6>Availability</h6>
                    <p>Reserved: <span>{formatMoney(dataForPayday?.payoutAvailability?.reservedAmount)}</span></p>
                    <p>Available: <span>{formatMoney(dataForPayday?.payoutAvailability?.availableAmount)}</span></p>
                    <p>Remaining payout
                        count: <span>{dataForPayday?.payoutAvailability == null ? '-' : (dataForPayday.payoutAvailability.availablePayoutCount ?? '∞')}</span>
                    </p>
                </DetailsCard>
                <DetailsCard>
                    <h6>Preliminary pay slip</h6>
                    <p>Gross pay: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.grossPay)}</span></p>
                    <p>Gross advances: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.grossAdvances)}</span></p>
                    <p>Tax: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.tax)}</span></p>
                    <p>Net advances: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.netAdvances)}</span></p>
                    <p>Gross employer initiated payouts: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.grossEmployerInitiatedPayouts)}</span></p>
                    <p>Net employer initiated payouts: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.netEmployerInitiatedPayouts)}</span></p>
                    <p>Fees: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.fees)}</span></p>
                    <p>Payout: <span>{formatMoney(dataForPayday?.preliminaryPayslipData?.payAfterDeductions)}</span></p>
                </DetailsCard>
            </>
        }
        {dataForPayday?.payoutBasisInfo &&
          <DetailsCard>
              <h6>AGI basis</h6>
              <p>Net: <span>{formatMoney(dataForPayday?.payoutBasisInfo?.netValue)}</span></p>
              <p>Gross: <span>{formatMoney(dataForPayday?.payoutBasisInfo?.grossValue)}</span></p>
              <p>Tax %: <span>{dataForPayday?.payoutBasisInfo?.payoutTaxPercent}</span></p>
          </DetailsCard>
        }
        {dataForPayday?.actualEarnings &&
          <DetailsCard>
              <h6>Actual pay</h6>
              <p>User total net pay: <span>{formatMoney(dataForPayday?.actualEarnings?.netEarnings)}</span></p>
              <p>Pay transaction net value: <span>{formatMoney(dataForPayday?.actualEarnings?.netPayTransactionAmount)}</span></p>
              <p>Net pay on payday after net deductions: <span>{formatMoney(dataForPayday?.actualEarnings?.netPayAmountAfterDeductions)}</span></p>
          </DetailsCard>
        }
      </CardSection>

      <CardSection>
        <PayArticleList payArticles={totalPayArticles}/>
      </CardSection>

      {dataForPayday?.employmentContracts?.map(ec => {
        const income = dataForPayday.income.employmentContracts.find(i => i.employmentContractId === ec.employmentContractId)
        const salary = dataForPayday.salary.employmentContracts.find(s => s.employmentContractId === ec.employmentContractId)
        const payArticles = dataForPayday.payArticles.employmentContracts.find(p => p.employmentContractId === ec.employmentContractId)
        const payouts = dataForPayday.payoutHistory.employmentContracts.find(p => p.employmentContractId === ec.employmentContractId)
        const availability = dataForPayday.payoutAvailability?.employmentContracts.find(a => a.employmentContractId === ec.employmentContractId)
        const contractPayArticles = salary == null
          ? payArticles?.payArticles ?? []
          : [...(payArticles?.payArticles ?? []), salary.payArticle]

        return (
          <div key={ec.employmentContractId} className={styles.employmentContractContainer}>
            <h4>Employment contract {ec.employmentContractId}</h4>

            <CardSection>
              <DetailsCard>
                <h6>Details</h6>
                <p>Id: <span>{ec.employmentContractId}</span></p>
                <p>External Id: <span>{ec.externalId ?? '-'}</span></p>
                <p>Description: <span>{ec.description ?? '-'}</span></p>
                <p>Salary
                  period: <span>{formatDateString(ec.salaryPayPeriodStartDate)} - {formatDateString(ec.salaryPayPeriodEndDate)}</span>
                </p>
                <p>Pay calculation type: <span>{ec.payCalculationType}</span></p>
              </DetailsCard>
              <DetailsCard>
                <h6>Income</h6>
                <p>Gross: <span>{formatMoney(income?.grossTotalAmount)}</span></p>
                <p>Net: <span>{formatNetMoney(income?.netTotalAmount, !isLocked)}</span></p>
                {salary && (
                  <>
                    <p>Gross salary: <span>{formatMoney(salary.grossCurrentValue)}</span></p>
                    <p>Net salary: <span>{formatNetMoney(salary.netCurrentValue, !isLocked)}</span></p>
                  </>
                )}
              </DetailsCard>
              <DetailsCard>
                <h6>Payouts</h6>
                <p>Total: <span>{formatMoney(payouts?.aggregatedAmount)}</span></p>
                <p>Month before payday: <span>{formatMoney(payouts?.aggregatedAmountMonthBeforePayday)}</span></p>
              </DetailsCard>
              {!isLocked &&
                  <DetailsCard>
                      <h6>Availability</h6>
                      <p>Available: <span>{formatMoney(availability?.availableAmount)}</span></p>
                      <p>Balance: <span>{formatMoney(availability?.balance)}</span></p>
                  </DetailsCard>
              }
            </CardSection>
            <CardSection>
              <PayArticleList payArticles={contractPayArticles}/>
            </CardSection>
          </div>
        )
      })}
    </div>
  )
}

export default EmployeePaydayDataDetails

const useStyles = createUseStyles({
  container: {
    '& h1': {
      marginBottom: titleMarginBottom
    },
    '& h4': {
      marginTop: '15px'
    }
  },
  actionBar: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  employmentContractContainer: {
    marginTop: '40px'
  },
  payArticleCard: {
    minWidth: '100%',
  },
  tableContainer: {
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: '0 0px',
    fontSize: '12px',
  },
  headerCell: {
    padding: '6px 12px',
    textAlign: 'left',
    fontFamily: 'Aestetico-Bold',
    lineHeight: 1.35,
  },
  bodyRow: {
    transition: '0.4s',
    '&:hover': {
      backgroundColor: Colors.darkGrayHover,
    },
  },
  bodyCell: {
    padding: '2px 12px',
    textAlign: 'left',
    fontFamily: 'Aestetico-Regular',
    lineHeight: 1.35,
    '&:first-child': {
      borderTopLeftRadius: '6px',
      borderBottomLeftRadius: '6px',
    },
    '&:last-child': {
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
    },
  },
  capitalize: {
    textTransform: 'capitalize'
  },
  statusIcon: {
    fontSize: '14px',
  }
})
